import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, HeartHandshake, MessageSquare, Send, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  FAMILY_RICHIESTA_STATI,
  familyRichiestaCategoria,
  familyRichiestaCodice,
  familyRichiestaDate,
  familyRichiestaStato,
  familyRichiestaWhen,
} from "../utils/familyRichieste";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUBTAB_CLASS =
  "gap-2 rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm whitespace-nowrap data-[state=active]:border-[#6e48b5] data-[state=active]:text-[#5a3b94] data-[state=active]:bg-transparent data-[state=active]:shadow-none";

function errorDetail(error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || item).join(" ");
  return detail || "Non è stato possibile salvare. Riprova.";
}

function StatusDot({ stato }) {
  const [, label, dot, text] = familyRichiestaStato(stato);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${text}`}>
      <i className={`h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function RichiestaCard({ item, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className={`flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition ${
        selected ? "border-[#6e48b5] ring-1 ring-[#6e48b5]/30" : "border-slate-200 hover:border-[#d8ccec]"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 text-[11px] text-[#8a9bb0]">
          <span>{familyRichiestaCodice(item)}</span>
          <span>{familyRichiestaDate(item.created_at)}</span>
        </div>
        <p className="mt-1 font-semibold text-slate-900">{item.oggetto || familyRichiestaCategoria(item.categoria)}</p>
        {item.preview ? <p className="mt-1 line-clamp-2 text-sm text-slate-500">“{item.preview}”</p> : null}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <StatusDot stato={item.stato} />
          <span className="text-xs text-slate-400">{item.ospite_nome || "Ospite"}{item.familiare_nome ? ` · ${item.familiare_nome}` : ""}</span>
        </div>
      </div>
      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-[#8ea0b3]" />
    </button>
  );
}

export default function FamilyStruttura() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "messaggi" ? "messaggi" : "ospiti";
  const selectedId = searchParams.get("richiesta") || "";
  const [items, setItems] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutte");
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [ospiti, inbox] = await Promise.all([
        axios.get(`${API_URL}/api/family/struttura/ospiti`, { headers }),
        axios.get(`${API_URL}/api/family/struttura/richieste`, { headers }),
      ]);
      setItems(ospiti.data.items || []);
      setRichieste(inbox.data.items || []);
      setError("");
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  useEffect(() => {
    if (!selectedId || !token) { setDetail(null); return undefined; }
    let cancelled = false;
    axios.get(`${API_URL}/api/family/struttura/richieste/${selectedId}`, { headers })
      .then((response) => { if (!cancelled) setDetail(response.data); })
      .catch((err) => { if (!cancelled) { setDetail(null); setError(errorDetail(err)); } });
    return () => { cancelled = true; };
  }, [selectedId, token]);

  const openCount = useMemo(() => richieste.filter((item) => item.stato !== "chiusa").length, [richieste]);
  const filtered = richieste.filter((item) => (
    filter === "tutte" ? true : filter === "aperte" ? item.stato !== "chiusa" : item.stato === "chiusa"
  ));

  const setTab = (next) => {
    const params = { tab: next };
    if (next === "messaggi" && selectedId) params.richiesta = selectedId;
    setSearchParams(params);
  };

  const selectRichiesta = (id) => setSearchParams({ tab: "messaggi", richiesta: id });

  const updateStato = async (stato) => {
    if (!selectedId) return;
    setSaving("stato"); setError("");
    try {
      await axios.patch(`${API_URL}/api/family/struttura/richieste/${selectedId}`, { stato }, { headers });
      await load();
      const response = await axios.get(`${API_URL}/api/family/struttura/richieste/${selectedId}`, { headers });
      setDetail(response.data);
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSaving("");
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selectedId || !reply.trim()) return;
    setSaving("reply"); setError("");
    try {
      await axios.post(`${API_URL}/api/family/struttura/richieste/${selectedId}/messaggi`, { messaggio: reply.trim() }, { headers });
      setReply("");
      await load();
      const response = await axios.get(`${API_URL}/api/family/struttura/richieste/${selectedId}`, { headers });
      setDetail(response.data);
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSaving("");
    }
  };

  const richiesta = detail?.richiesta;
  const messaggi = detail?.messaggi || [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <p className="text-sm font-semibold text-[#6e48b5]">AFFIDYA Family</p>
        <h1 className="text-3xl font-bold text-slate-900">Familiari e deleghe</h1>
        <p className="mt-2 text-slate-600">Gestisci gli ospiti abilitati al portale e i messaggi inviati dai familiari.</p>
      </div>

      {error ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex h-auto w-full flex-nowrap justify-start gap-0 overflow-x-auto rounded-none border-b border-slate-200 bg-transparent p-0">
          <TabsTrigger value="ospiti" className={SUBTAB_CLASS}>
            <Users className="h-4 w-4" />
            Ospiti abilitati
            <span className="rounded-full bg-[#EDE9F6] px-1.5 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{items.length}</span>
          </TabsTrigger>
          <TabsTrigger value="messaggi" className={SUBTAB_CLASS}>
            <MessageSquare className="h-4 w-4" />
            Messaggi
            {openCount ? <span className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">{openCount}</span> : (
              <span className="rounded-full bg-[#EDE9F6] px-1.5 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{richieste.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ospiti" className="mt-0">
          {loading ? <p className="text-slate-500">Caricamento…</p> : items.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((item) => (
                <Link key={item.fascicolo_id} to={`/fascicoli-assistenziali/${item.fascicolo_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-[#d8ccec]">
                  <HeartHandshake className="h-6 w-6 text-[#6e48b5]" />
                  <h2 className="mt-3 font-bold text-slate-900">{item.ospite_nome}</h2>
                  <p className="mt-1 text-sm text-slate-500"><Users className="mr-1 inline h-4 w-4" />{item.deleghe_attive} deleghe attive</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
              Nessun ospite è ancora abilitato a AFFIDYA Family. Apri un fascicolo e usa la scheda Family.
            </div>
          )}
        </TabsContent>

        <TabsContent value="messaggi" className="mt-0">
          {loading ? <p className="text-slate-500">Caricamento…</p> : (
            <div className={`grid gap-4 ${selectedId ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]" : ""}`}>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {[["tutte", "Tutte"], ["aperte", "Aperte"], ["chiuse", "Chiuse"]].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFilter(value)}
                      className={`rounded-full px-3 py-1.5 text-sm font-semibold ${filter === value ? "bg-[#6e48b5] text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      {label}
                      {value === "aperte" && openCount ? <sup className="ml-1 rounded-full bg-rose-500 px-1.5 text-[10px] text-white">{openCount}</sup> : null}
                    </button>
                  ))}
                </div>
                {filtered.length ? filtered.map((item) => (
                  <RichiestaCard key={item.id} item={item} selected={item.id === selectedId} onSelect={selectRichiesta} />
                )) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
                    Nessun messaggio in questa vista.
                  </div>
                )}
              </div>

              {selectedId ? (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  {richiesta ? (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] text-[#8a9bb0]">{familyRichiestaCodice(richiesta)} · {familyRichiestaDate(richiesta.created_at)}</p>
                          <h2 className="mt-1 text-lg font-bold text-slate-900">{richiesta.oggetto}</h2>
                          <p className="mt-1 text-sm text-slate-500">{familyRichiestaCategoria(richiesta.categoria)} · {richiesta.ospite_nome || "Ospite"} · {richiesta.familiare_nome}</p>
                        </div>
                        <StatusDot stato={richiesta.stato} />
                      </div>

                      <div className="mt-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Stato</p>
                        <div className="flex flex-wrap gap-2">
                          {FAMILY_RICHIESTA_STATI.map(([value, label]) => (
                            <Button
                              key={value}
                              type="button"
                              variant={richiesta.stato === value ? "default" : "outline"}
                              disabled={saving === "stato"}
                              onClick={() => updateStato(value)}
                              className={richiesta.stato === value
                                ? "h-8 rounded-full bg-[#6e48b5] px-3 text-xs hover:bg-[#5a3b94]"
                                : "h-8 rounded-full border-[#d8ccec] px-3 text-xs text-[#5a3b94] hover:bg-[#EDE9F6]"}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-5 max-h-[420px] space-y-3 overflow-y-auto border-t border-slate-100 pt-4">
                        {messaggi.map((message) => (
                          <article key={message.id} className={`rounded-xl p-3 ${message.autore_tipo === "struttura" ? "bg-[#F7F5FB]" : "bg-slate-50"}`}>
                            <p className="text-xs font-semibold text-slate-600">{message.autore_nome || (message.autore_tipo === "struttura" ? "Struttura" : "Familiare")} · {familyRichiestaWhen(message.created_at)}</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{message.messaggio}</p>
                          </article>
                        ))}
                      </div>

                      {richiesta.stato === "chiusa" ? (
                        <p className="mt-4 text-sm text-slate-500">La richiesta è chiusa. Cambia stato per rispondere di nuovo.</p>
                      ) : (
                        <form onSubmit={sendReply} className="mt-4 space-y-3">
                          <Textarea
                            rows={3}
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Scrivi la risposta per il familiare"
                            className="min-h-[88px] rounded-xl border-slate-200"
                          />
                          <Button disabled={saving === "reply" || !reply.trim()} className="h-10 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
                            <Send className="h-4 w-4" />
                            {saving === "reply" ? "Invio…" : "Invia risposta"}
                          </Button>
                        </form>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Caricamento messaggio…</p>
                  )}
                </section>
              ) : null}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
